// export default Index;

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardBody, CardFooter, CardHeader } from '@components/card/card';
import Breadcrumb from '@components/Shared/Breadcrumb';
import { useTranslation } from 'react-i18next';
import AddMasterItems from './AddMasterItems';
import Loading from '@components/Shared/Loader/Loading';
import { usePermissions } from '@Helper/Permisstions/usePermissions';
import NeatixModal from '@components/Modal/NeatixModal';
import UpdateItem from './ItemsDetails/UpdateItem';
import { DynamicFilter } from '@Helper/DynamicFilter/DynamicFilter';
import { DynamicPagination } from '@Helper/DynamicFilter/DynamicPagination';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { getPagedData, setEndpoint, setFixedFilters } from '@store/DynamicSlice/dynamicTableSlice';
import useGetCategoriesList from '@hooks/INV/useGetCategoriesList';

const TABLE_KEY = 'InvMasterItems';

const Index = () => {
  const { HasPermission } = usePermissions();
  const [Show, setShow] = useState(false);
  const [Show2, setShow2] = useState(false);
  const [item, setItem] = useState<any>();
  const { t, i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('name'); // ✅ نوع البحث الافتراضي
  const [showFilters, setShowFilters] = useState(false);

  const [isUsingFixedFilters, setIsUsingFixedFilters] = useState(false);

  const { CategoriesOptions } = useGetCategoriesList();

  const dispatch = useAppDispatch();
  const tableState = useAppSelector(state => state.dynamicTable[TABLE_KEY]);
  const data = tableState?.data ?? [];
  const loading = tableState?.loading ?? false;
  const error = tableState?.error ?? null;
  const totalItems = tableState?.totalItems ?? 0;

  useEffect(() => {
    dispatch(setEndpoint({ tableKey: TABLE_KEY, endpoint: 'Inv/GetMasterItemsListPaged' }));

    dispatch(
      setFixedFilters({
        tableKey: TABLE_KEY,
        fixedFilters: {}
      })
    );

    dispatch(getPagedData({ tableKey: TABLE_KEY }));
  }, [dispatch]);

  // ✅ خيارات أنواع البحث
  const searchTypeOptions = [
    { value: 'name', label: t('ItemName') },
    { value: 'itemCode', label: t('ItemCode') },
    { value: 'barcode', label: t('Barcode') }
    // { value: 'categoryId', label: t('Category') },
    // { value: 'unitOfMeasure', label: t('UnitOfMeasure') },
    // { value: 'brandId', label: t('Brand') },
    // { value: 'itemType', label: t('ItemType') },
  ];

  // ✅ دالة البحث باستخدام fixedFilters
  const handleSearch = () => {
    const trimmedValue = searchTerm.trim();

    if (trimmedValue === '') {
      clearSearch();
      return;
    }

    const filters: Record<string, any> = {};

    // ✅ تحديد الحقل المناسب بناءً على نوع البحث
    let field = searchType;

    // ✅ معالجة الحقول الخاصة
    if (searchType === 'name') {
      field = i18n.language === 'en' ? 'name' : 'name2';
    }

    // ✅ إذا كان البحث برقم (UUID) أو نص عادي
    if (['categoryId', 'unitOfMeasure', 'brandId'].includes(searchType)) {
      // نتحقق إذا كان النص هو UUID
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (uuidRegex.test(trimmedValue)) {
        filters[field] = trimmedValue;
      } else {
        // إذا لم يكن UUID، قد نبحث بالاسم بدلاً من المعرف
        alert(t('PleaseEnterValidUUID'));
        return;
      }
    } else if (searchType === 'itemType') {
      // ✅ البحث بنوع الصنف
      const itemTypeOptions = ['AssemblyItem', 'ServiceItem', 'FixedAssetItem', 'ExpenseItem', 'InventoryItem'];

      if (itemTypeOptions.includes(trimmedValue)) {
        filters[field] = trimmedValue;
      } else {
        // البحث بأي نص في حقل itemType
        filters[field] = trimmedValue;
      }
    } else {
      // ✅ البحث العادي (النص)
      filters[field] = trimmedValue;
    }

    dispatch(
      setFixedFilters({
        tableKey: TABLE_KEY,
        fixedFilters: filters
      })
    );
    setIsUsingFixedFilters(true);

    dispatch(getPagedData({ tableKey: TABLE_KEY }));
  };

  // ✅ دالة البحث عند الضغط على Enter
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // ✅ دالة مسح البحث
  const clearSearch = (): void => {
    setSearchTerm('');

    dispatch(
      setFixedFilters({
        tableKey: TABLE_KEY,
        fixedFilters: {}
      })
    );
    setIsUsingFixedFilters(false);

    dispatch(getPagedData({ tableKey: TABLE_KEY }));
  };

  const displayData = isUsingFixedFilters ? data : data;

  const getItemTypeBadge = (itemType: string) => {
    const typeConfig: any = {
      AssemblyItem: { color: 'primary', text: t('AssemblyItem'), icon: 'fas fa-puzzle-piece' },
      ServiceItem: { color: 'success', text: t('ServiceItem'), icon: 'fas fa-tools' },
      FixedAssetItem: { color: 'warning', text: t('FixedAssetItem'), icon: 'fas fa-building' },
      ExpenseItem: { color: 'info', text: t('ExpenseItem'), icon: 'fas fa-cash-register' },
      InventoryItem: { color: 'secondary', text: t('InventoryItem'), icon: 'fas fa-box' }
    };

    const config = typeConfig[itemType] || { color: 'dark', text: itemType, icon: 'fas fa-question-circle' };

    return (
      <span
        className={`badge d-block rounded px-2 py-1 text-wrap text-center fw-bold 
                  bg-${config.color} bg-opacity-20 text-${config.color}`}
        style={{ minWidth: '80px', maxWidth: '120px', display: 'inline-block' }}
      >
        <i className={`${config.icon} fa-fw me-1`}></i>
        {config.text}
      </span>
    );
  };

  const getAvatarLetter = (item: any): string => {
    const name = i18n.language === 'en' ? item.name : item.name2;
    return name?.charAt(0)?.toUpperCase() || '?';
  };

  const getAvatarColor = (itemType: string): string => {
    const colorMap: any = {
      AssemblyItem: 'primary',
      ServiceItem: 'success',
      FixedAssetItem: 'warning',
      ExpenseItem: 'info',
      InventoryItem: 'secondary'
    };
    return colorMap[itemType] || 'dark';
  };

  const totalItemsCount: number = totalItems;
  const inventoryItems: number = data?.filter((item: any) => item.itemType === 'InventoryItem').length || 0;
  const serviceItems: number = data?.filter((item: any) => item.itemType === 'ServiceItem').length || 0;
  const assemblyItems: number = data?.filter((item: any) => item.itemType === 'AssemblyItem').length || 0;

  const fields: any[] = [
    { name: 'name2', label: t('ItemName'), type: 'string' },
    { name: 'itemCode', label: t('ItemCode'), type: 'string' },
    { name: 'barcode', label: t('Barcode'), type: 'string' },
    {
      name: 'itemType',
      label: t('ItemType'),
      type: 'options',
      options: [
        { value: 'AssemblyItem', label: t('AssemblyItem') },
        { value: 'ServiceItem', label: t('ServiceItem') },
        { value: 'FixedAssetItem', label: t('FixedAssetItem') },
        { value: 'ExpenseItem', label: t('ExpenseItem') },
        { value: 'InventoryItem', label: t('InventoryItem') }
      ]
    },
    {
      name: 'categoryId',
      label: t('Category'),
      type: 'options',
      options: CategoriesOptions
    }
  ];

  const breadcrumbList: any[] = [
    { name: t('Home'), link: ':moduleHome' },
    { name: t('Items'), link: null }
  ];

  return (
    <>
      <div className='d-flex flex-wrap align-items-center gap-2'>
        <Breadcrumb BreadcrumbList={breadcrumbList} PageName={t('Items')} />

        <div className='ms-auto'>
          {HasPermission('INVAddMasterItems') && (
            <Link to='' className='btn btn-theme' onClick={() => setShow(true)}>
              <i className='fa fa-plus-circle fa-fw me-1'></i>
              {t('AddNewItem')}
            </Link>
          )}
        </div>
      </div>

      {/* بطاقات الإحصائيات */}
      <div className='row mb-4'>
        <div className='col-6 col-lg-3'>
          <Card className='bg-primary bg-opacity-10 border-primary border-0 shadow-sm h-100'>
            <CardBody className='py-3'>
              <div className='d-flex align-items-center'>
                <div className='flex-shrink-0'>
                  <i className='fas fa-boxes fa-2x text-primary'></i>
                </div>
                <div className='flex-grow-1 ms-3'>
                  <h4 className='text-primary mb-0'>{totalItemsCount}</h4>
                  <p className='text-muted mb-0'>{t('TotalItems')}</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div className='col-6 col-lg-3'>
          <Card className='bg-success bg-opacity-10 border-success border-0 shadow-sm h-100'>
            <CardBody className='py-3'>
              <div className='d-flex align-items-center'>
                <div className='flex-shrink-0'>
                  <i className='fas fa-box fa-2x text-success'></i>
                </div>
                <div className='flex-grow-1 ms-3'>
                  <h4 className='text-success mb-0'>{inventoryItems}</h4>
                  <p className='text-muted mb-0'>{t('InventoryItems')}</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div className='col-6 col-lg-3'>
          <Card className='bg-info bg-opacity-10 border-info border-0 shadow-sm h-100'>
            <CardBody className='py-3'>
              <div className='d-flex align-items-center'>
                <div className='flex-shrink-0'>
                  <i className='fas fa-tools fa-2x text-info'></i>
                </div>
                <div className='flex-grow-1 ms-3'>
                  <h4 className='text-info mb-0'>{serviceItems}</h4>
                  <p className='text-muted mb-0'>{t('ServiceItems')}</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div className='col-6 col-lg-3'>
          <Card className='bg-warning bg-opacity-10 border-warning border-0 shadow-sm h-100'>
            <CardBody className='py-3'>
              <div className='d-flex align-items-center'>
                <div className='flex-shrink-0'>
                  <i className='fas fa-puzzle-piece fa-2x text-warning'></i>
                </div>
                <div className='flex-grow-1 ms-3'>
                  <h4 className='text-warning mb-0'>{assemblyItems}</h4>
                  <p className='text-muted mb-0'>{t('AssemblyItems')}</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>

      <Card className='mb-4'>
        <CardHeader>
          <div className='d-flex flex-wrap align-items-center gap-2 ms-auto'>
            {/* ✅ سيليكت لاختيار نوع البحث */}
            <div className='input-group input-group' style={{ minWidth: '180px', maxWidth: '200px' }}>
              <span className='input-group-text  '>
                <i className='fas fa-filter text-muted'></i>
              </span>
              <select
                className='form-select border-start-0'
                value={searchType}
                onChange={e => setSearchType(e.target.value)}
                style={{ cursor: 'pointer' }}
              >
                {searchTypeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* ✅ شريط البحث مع زر البحث */}
            <div className='input-group input-group' style={{ minWidth: '250px', maxWidth: '350px' }}>
              <span className='input-group-text'>
                <i className='fas fa-search text-muted'></i>
              </span>
              <input
                type='text'
                className='form-control border-start-0'
                placeholder={t('SearchItemsPlaceholder')}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyPress}
              />
              {searchTerm ? (
                <>
                  <button
                    className='btn btn-outline-secondary'
                    type='button'
                    onClick={clearSearch}
                    title={t('ClearSearch')}
                  >
                    <i className='fas fa-times'></i>
                  </button>
                  <button className='btn btn-theme' type='button' onClick={handleSearch} title={t('Search')}>
                    <i className='fas fa-search'></i>
                  </button>
                </>
              ) : (
                <button className='btn btn-theme' type='button' onClick={handleSearch} title={t('Search')}>
                  <i className='fas fa-search'></i>
                </button>
              )}
            </div>

                <button
        className='btn btn-outline-theme  w-md-auto'
        onClick={() => setShowFilters(true)}
        title={t('SearchFilters')}
      >
        <i className='fas fa-filter fa-fw me-1'></i>
        {t('Filters')}
      </button> 
          </div>
        </CardHeader>
 

        <Loading loading={loading} error={error}>
          <div className='table-responsive'>
            <table className='table table-hover mb-0'>
              <thead className='bg-theme bg-opacity-25 fw-bold'>
                <tr>
                  <th className='small' style={{ minWidth: '250px', maxWidth: '300px' }}>
                    {t('ItemName')}
                  </th>
                  <th className='small' style={{ minWidth: '120px' }}>
                    {t('ItemCode')}
                  </th>
                  <th className='small' style={{ minWidth: '150px' }}>
                    {t('Category')}
                  </th>
                  <th className='small' style={{ minWidth: '150px' }}>
                    {t('ItemType')}
                  </th>
                  <th className='small text-end' style={{ minWidth: '150px' }}>
                    {t('Actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayData.length > 0 ? (
                  displayData.map((item: any) => (
                    <tr key={item.id}>
                      {/* خلية اسم الصنف مع معالجة النصوص الطويلة */}
                      <td className='align-middle'>
                        <div className='d-flex align-items-center'>
                          <div
                            className={`rounded-2 bg-${getAvatarColor(
                              item.itemType
                            )} bg-opacity-20 text-${getAvatarColor(
                              item.itemType
                            )} d-flex justify-content-center align-items-center me-3 flex-shrink-0`}
                            style={{ width: '40px', height: '40px', fontWeight: 'bold', fontSize: '1.1rem' }}
                          >
                            {getAvatarLetter(item)}
                          </div>

                          <div className='d-flex flex-column' style={{ minWidth: 0, maxWidth: '280px' }}>
                            {/* الاسم الرئيسي مع تحديد النص */}
                            <strong
                              className='fw-bold text-truncate'
                              title={i18n.language === 'en' ? item.name : item.name2}
                              style={{ maxWidth: '100%' }}
                            >
                              {i18n.language === 'en' ? item.name : item.name2}
                            </strong>
                          </div>
                        </div>
                      </td>

                      <td className='align-middle'>
                        <strong className='text-primary'>{item.itemCode}</strong>
                      </td>

                      <td className='align-middle'>
                        <span
                          className='badge bg-info bg-opacity-20 text-info text-truncate d-inline-block'
                          style={{ maxWidth: '120px' }}
                          title={i18n.language === 'en' ? item.categoryName : item.categoryName2}
                        >
                          {i18n.language === 'en' ? item.categoryName : item.categoryName2}
                        </span>
                      </td>

                      <td className='align-middle'>
                        <div style={{ maxWidth: '120px' }}>{getItemTypeBadge(item.itemType)}</div>
                      </td>

                      <td className='align-middle text-end'>
                        <div className='d-flex justify-content-end gap-2'>
                          {HasPermission('INVViewMasterItemsDetails') && (
                            <button
                              type='button'
                              className='btn btn-sm btn-outline-theme'
                              onClick={() => {
                                setShow2(true);
                                setItem(item);
                              }}
                              title={t('ViewDetails')}
                            >
                              <i className='fas fa-eye fa-fw me-1'></i>
                              {t('Show')}
                            </button>
                          )}

                          {HasPermission('INVDeleteMasterItems') && (
                            <button
                              type='button'
                              className='btn btn-sm btn-outline-danger'
                              onClick={() => {
                                /* دالة الحذف */
                              }}
                              title={t('Delete')}
                            >
                              <i className='fas fa-trash fa-fw me-1'></i>
                              {t('Delete')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className='text-center py-4 text-muted'>
                      <i className='fa fa-inbox fa-2x mb-2'></i>
                      <br />
                      {isUsingFixedFilters ? t('NoSearchResults') : t('NoItemsRegistered')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Loading>

        <CardFooter>
          <div className='text-theme'>
            <DynamicPagination tableKey={TABLE_KEY} maxVisiblePages={3} showItemsPerPage={true} />
          </div>
        </CardFooter>
      </Card>

      <DynamicFilter tableKey={TABLE_KEY} fields={fields} show={showFilters} onClose={() => setShowFilters(false)} />

      <NeatixModal setShow={setShow} show={Show} size='xl' showHeader={false} backdrop>
        <AddMasterItems onclose={() => setShow(false)} openModalEdit={() => setShow2(true)} setItem={setItem} />
      </NeatixModal>

      <NeatixModal setShow={setShow2} show={Show2} size='xl' showHeader={false}>
        {item && <UpdateItem onClose={() => setShow2(false)} item={item} />}
      </NeatixModal>
    </>
  );
};

export default Index;
